import { useCallback, useRef, useState } from "react";
import type { DOMAttributes, RefObject } from "react";

/** Velocidades, na ordem em que a pílula cicla (as mesmas do WhatsApp). */
export const RATES = [1, 1.5, 2] as const;
export type Rate = (typeof RATES)[number];

/**
 * Garante que só uma bolha toque por vez (como no Telegram).
 *
 * É estado de MÓDULO de propósito: a exclusão vale entre INSTÂNCIAS diferentes
 * do player, então nao pode virar estado de componente nem ref.
 */
let activeAudio: HTMLAudioElement | null = null;

function listenedKey(seed: string): string {
  return `zapir:voice-listened:${seed}`;
}

interface AudioPlaybackOptions {
  url: string | null;
  /** Chave do "já ouvido" no localStorage (ex. `message.id`). */
  seed: string;
  /** Duração conhecida por fora (decode da onda / payload), em segundos. */
  fallbackDurationSec: number | null;
}

interface AudioPlayback {
  audioRef: RefObject<HTMLAudioElement | null>;
  /** Handlers do `<audio>`; o JSX só espalha (`<audio {...audioProps} />`). */
  audioProps: DOMAttributes<HTMLAudioElement>;
  playing: boolean;
  current: number;
  rate: Rate;
  failed: boolean;
  listened: boolean;
  /** `loadedmetadata` primeiro, senão o que o chamador souber. */
  durationSec: number | null;
  /** 0..1, já limitado. */
  progress: number;
  toggle: () => void;
  seekTo: (fraction: number) => void;
  toggleRate: () => void;
  /** Zera tempo/estado ao trocar de áudio. Estável, dá para pôr em deps. */
  reset: () => void;
}

/**
 * Controle do elemento `<audio>` da bolha de voz: play/pause exclusivo entre
 * bolhas, busca, velocidade, marca de "já ouvido" e tempo corrente. Fica fora
 * do componente para o JSX ser só apresentação.
 */
export function useAudioPlayback({ url, seed, fallbackDurationSec }: AudioPlaybackOptions): AudioPlayback {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [metaDuration, setMetaDuration] = useState<number | null>(null);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState<Rate>(1);
  const [failed, setFailed] = useState(false);
  const [listened, setListened] = useState(() => {
    try {
      return window.localStorage.getItem(listenedKey(seed)) === "1";
    } catch {
      return false;
    }
  });

  const durationSec = metaDuration ?? fallbackDurationSec;
  const progress = durationSec && durationSec > 0 ? Math.min(1, Math.max(0, current / durationSec)) : 0;

  const reset = useCallback(() => {
    setMetaDuration(null);
    setCurrent(0);
    setPlaying(false);
    setFailed(false);
  }, []);

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !url) return;
    if (audio.paused) {
      if (activeAudio && activeAudio !== audio) activeAudio.pause();
      activeAudio = audio;
      audio.playbackRate = rate;
      void audio.play().catch(() => setFailed(true));
    } else {
      audio.pause();
    }
  }, [rate, url]);

  const seekTo = useCallback(
    (fraction: number) => {
      const audio = audioRef.current;
      if (!audio || !url) return;
      const clamped = Math.min(0.999, Math.max(0, fraction));
      if (durationSec && durationSec > 0) {
        audio.currentTime = clamped * durationSec;
        setCurrent(audio.currentTime);
      } else {
        // Sem duração conhecida: busca proporcional ao que o elemento souber.
        const total = Number.isFinite(audio.duration) ? audio.duration : 0;
        if (total > 0) audio.currentTime = clamped * total;
      }
    },
    [durationSec, url],
  );

  const toggleRate = useCallback(() => {
    setRate((currentRate) => {
      const next = RATES[(RATES.indexOf(currentRate) + 1) % RATES.length]!;
      if (audioRef.current) audioRef.current.playbackRate = next;
      return next;
    });
  }, []);

  const audioProps: DOMAttributes<HTMLAudioElement> = {
    onLoadedMetadata: (event) => {
      const total = event.currentTarget.duration;
      if (Number.isFinite(total) && total > 0) setMetaDuration(total);
    },
    onTimeUpdate: (event) => setCurrent(event.currentTarget.currentTime),
    onPlay: () => {
      setPlaying(true);
      setFailed(false);
    },
    onPause: () => setPlaying(false),
    onEnded: () => {
      setPlaying(false);
      setListened(true);
      try {
        window.localStorage.setItem(listenedKey(seed), "1");
      } catch {
        /* privado: segue sem persistir */
      }
      if (activeAudio && activeAudio.paused) activeAudio = null;
    },
    onError: () => setFailed(true),
  };

  return {
    audioRef,
    audioProps,
    playing,
    current,
    rate,
    failed,
    listened,
    durationSec,
    progress,
    toggle,
    seekTo,
    toggleRate,
    reset,
  };
}
